import { useId, type ComponentProps } from 'preact/compat'
import { InfoPopover } from './InfoPopover'

interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type'> {
  label?: string | preact.JSX.Element
  className?: string
  infoText?: string
  error?: boolean
  errorMessage?: string
}

export function Checkbox({
  label,
  className = '',
  infoText,
  error = false,
  errorMessage,
  id,
  checked,
  disabled,
  required,
  ...props
}: CheckboxProps) {
  const checkboxId = id ?? useId()

  return (
    <div className={className}>
      <div className='flex items-start gap-2.5'>
        <label htmlFor={checkboxId} className='relative flex items-center'>
          <input
            type='checkbox'
            id={checkboxId}
            className='sr-only'
            checked={checked}
            disabled={disabled}
            required={required}
            {...props}
          />
          <span
            className={`relative flex items-center justify-center w-5 h-5 min-w-[17px] border rounded-lg cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-offset-2 ${
              error
                ? 'focus-within:ring-red-300'
                : 'focus-within:ring-primary-300'
            } ${
              checked
                ? error
                  ? 'bg-red-600 border-red-600'
                  : 'bg-cencert-navy-blue-light border-cencert-navy-blue-light'
                : error
                  ? 'bg-white border-red-500 hover:border-red-600'
                  : 'bg-white border-gray-400 hover:border-gray-500'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-hidden='true'
          >
            <svg
              className={`w-2.5 h-2.5 text-white transition-opacity duration-200 pointer-events-none ${
                checked ? 'opacity-100' : 'opacity-0'
              }`}
              viewBox='0 0 12 10'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M1 5L4.5 8.5L11 1.5'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </span>
          {label && (
            <span
              className={`ml-2.5 text-label cursor-pointer select-none ${
                error ? 'text-red-700' : 'text-gray-700'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {label}
              {required && <span className='text-red-600'> *</span>}
            </span>
          )}
        </label>
        {label && infoText && (
          <InfoPopover infoText={infoText} placement='right' />
        )}
      </div>
      {error && errorMessage && (
        <p className='text-sm text-red-600 mt-1.5 ml-7'>{errorMessage}</p>
      )}
    </div>
  )
}
